import type { RemovableRef } from '@vueuse/core';
import type { MaybeRefOrGetter } from 'vue';

import { useStorage } from '@vueuse/core';
import { toValue } from 'vue';

import type { OrderSource } from '@shared/api/graphql/generated';

export interface FiscalWorkstationLocalState {
  browserWorkstationId: string;
  pendingOperationIds: string[];
  /**
   * Заказ каждой незавершённой операции: витрина после перезагрузки знает только их.
   * Имя — часть сохранённого состояния кассового компьютера; не переименовывать.
   */
  pendingSaleIds: Record<string, string>;
  /**
   * Источник заказа операции: страница продолжает только свою незавершённую оплату.
   * Операция без источника сохранена прежней версией и подходит любой странице.
   */
  pendingOrderSources: Record<string, OrderSource>;
}

interface FiscalWorkstationStorageDependencies {
  createId?: () => string;
  storage?: Storage;
}

export interface FiscalWorkstationStorage {
  addPendingOperation: (operationId: string, orderId?: string, source?: OrderSource) => void;
  clearPendingOperation: (operationId: string) => void;
  clearPendingOrder: (orderId: string) => void;
  pendingOperationIds: () => readonly string[];
  /** Без источника — все заказы, с источником — только записанные с ним. */
  pendingSaleIds: (source?: OrderSource) => readonly string[];
  state: RemovableRef<FiscalWorkstationLocalState>;
}

export function fiscalWorkstationStorageKey(organizationId: string, locationId: string): string {
  return `mors:fiscalization:${organizationId}:${locationId}:workstation`;
}

/**
 * `useStorage` вешает слушатели `storage` и custom-event и пишет дефолт при создании,
 * поэтому на одну пару организация/локация нужен ровно один экземпляр: иначе каждый
 * вызывающий плодит подписки, которые никто не снимает. Кэш обходится, когда переданы
 * зависимости — так тесты остаются изолированными.
 */
const storagesByKey = new Map<string, FiscalWorkstationStorage>();

export function useFiscalWorkstationStorage(
  organizationId: string,
  locationId: string,
  dependencies: FiscalWorkstationStorageDependencies = {},
): FiscalWorkstationStorage {
  const cacheKey = fiscalWorkstationStorageKey(organizationId, locationId);
  const shared = Object.keys(dependencies).length === 0;
  const cached = shared ? storagesByKey.get(cacheKey) : undefined;

  if (cached !== undefined) return cached;

  const createId = dependencies.createId ?? createWorkstationId;
  const storage = dependencies.storage ?? localStorage;
  const state = useStorage<FiscalWorkstationLocalState>(
    cacheKey,
    {
      browserWorkstationId: createId(),
      pendingOperationIds: [],
      pendingSaleIds: {},
      pendingOrderSources: {},
    },
    storage,
    {
      mergeDefaults: true,
      writeDefaults: true,
    },
  );

  const addPendingOperation = (operationId: string, orderId?: string, source?: OrderSource): void => {
    if (operationId.trim() === '') return;

    const { pendingOperationIds, pendingOrderSources, pendingSaleIds } = state.value;
    const known = pendingOperationIds.includes(operationId);
    const orderKnown = orderId === undefined || pendingSaleIds[operationId] === orderId;
    const sourceKnown = source === undefined || pendingOrderSources[operationId] === source;
    if (known && orderKnown && sourceKnown) return;

    state.value = {
      ...state.value,
      pendingOperationIds: known ? pendingOperationIds : [...pendingOperationIds, operationId],
      pendingSaleIds: orderId === undefined ? pendingSaleIds : { ...pendingSaleIds, [operationId]: orderId },
      pendingOrderSources:
        source === undefined ? pendingOrderSources : { ...pendingOrderSources, [operationId]: source },
    };
  };
  const clearPendingOperation = (operationId: string): void => {
    const without = <T>(record: Record<string, T>): Record<string, T> =>
      Object.fromEntries(Object.entries(record).filter(([id]) => id !== operationId));

    state.value = {
      ...state.value,
      pendingOperationIds: state.value.pendingOperationIds.filter((id) => id !== operationId),
      pendingSaleIds: without(state.value.pendingSaleIds),
      pendingOrderSources: without(state.value.pendingOrderSources),
    };
  };
  const clearPendingOrder = (orderId: string): void => {
    for (const [operationId, pendingOrderId] of Object.entries(state.value.pendingSaleIds)) {
      if (pendingOrderId === orderId) clearPendingOperation(operationId);
    }
  };

  const storageApi: FiscalWorkstationStorage = {
    addPendingOperation,
    clearPendingOperation,
    clearPendingOrder,
    pendingOperationIds: () => [...state.value.pendingOperationIds],
    pendingSaleIds: (source) => [
      ...new Set(
        Object.entries(state.value.pendingSaleIds)
          .filter(([operationId]) => {
            const recorded = state.value.pendingOrderSources[operationId];

            return source === undefined || recorded === undefined || recorded === source;
          })
          .map(([, orderId]) => orderId),
      ),
    ],
    state,
  };

  if (shared) storagesByKey.set(cacheKey, storageApi);

  return storageApi;
}

function createWorkstationId(): string {
  const id = globalThis.crypto?.randomUUID();

  if (id === undefined) {
    throw new Error('Браузер не поддерживает создание идентификатора рабочего места.');
  }

  return id;
}

const kassaLocationKey = (organizationId: string) => `mors:fiscalization:${organizationId}:kassa-location`;

/**
 * Локация, кассой которой подтверждённо работает этот браузер: переключение с неё спрашивает
 * подтверждение. Пишет плашка, когда касса этого компьютера отвечает; недоступное хранилище — без отметки.
 */
export function useKassaLocationMarker(organizationId: MaybeRefOrGetter<string | null | undefined>) {
  const key = (): string | null => {
    const id = toValue(organizationId);

    return id ? kassaLocationKey(id) : null;
  };
  const locationId = (): string | null => {
    const storageKey = key();
    try {
      return storageKey ? localStorage.getItem(storageKey) : null;
    } catch {
      return null;
    }
  };
  const write = (value: string | null): void => {
    const storageKey = key();
    if (!storageKey) return;
    try {
      if (value === null) localStorage.removeItem(storageKey);
      else localStorage.setItem(storageKey, value);
    } catch {
      // Хранилище недоступно: отметки нет, переключение без подтверждения.
    }
  };

  return {
    locationId,
    mark: (id: string): void => write(id),
    /** Снимает отметку, только если она стоит на этой же локации. */
    clear: (id: string): void => {
      if (locationId() === id) write(null);
    },
  };
}

export type KassaLocationMarker = ReturnType<typeof useKassaLocationMarker>;
